import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Modal as BSModal } from 'react-bootstrap';
import { Typography, NewIconButton, NewDropdown } from 'app/components';

import './Modal.scss';

type AccuracyValue = number | 'INF';

const parseAccuracyValue = (value: string): AccuracyValue | null => {
  if (value.toUpperCase() === 'INF') return 'INF';
  const num = Number(value);
  if (!Number.isInteger(num) || num < -15 || num > 15) return null;
  return num;
};

const isValidAccuracyValue = (value: string): boolean => parseAccuracyValue(value) !== null;

interface AddPropertyModalProps {
  show: boolean;
  availableTypes: ('scale' | 'decimals' | 'precision' | 'format')[];
  editData?: {
    id: string;
    type: 'scale' | 'decimals' | 'precision' | 'format';
    value: number | string;
  } | null;
  onClose: () => void;
  onAdd: (data: { type: 'scale' | 'decimals' | 'precision' | 'format'; value: number | string }) => void;
  onEdit?: (data: { type: 'scale' | 'decimals' | 'precision' | 'format'; value: number | string }) => void;
}

export const AddPropertyModal = ({ show, availableTypes, editData, onClose, onAdd, onEdit }: AddPropertyModalProps) => {
  const { t } = useTranslation(['common', 'tag']);

  const isEditMode = !!editData;

  const [formatType, setFormatType] = useState<'scale' | 'decimals' | 'precision' | 'format'>('scale');
  const [scaleValue, setScaleValue] = useState<number>(0);
  const [decimalsValue, setDecimalsValue] = useState<string>('0');
  const [precisionValue, setPrecisionValue] = useState<string>('0');
  const [formatValue, setFormatValue] = useState<string>('');

  const FORMAT_OPTIONS = [
    { label: 'num-dot-decimal (1,234.56)', value: 'ixt:num-dot-decimal' },
    { label: 'num-comma-decimal (1.234,56)', value: 'ixt:num-comma-decimal' },
    { label: 'num-unit-decimal (1 234,56)', value: 'ixt:num-unit-decimal' },
    { label: 'fixed-zero (–)', value: 'ixt:fixed-zero' },
    { label: 'num-words-en (twelve thousand)', value: 'ixt:num-words-en' },
  ];

  // Pre-fill for edit mode or reset to first available option when modal opens
  useEffect(() => {
    if (show && editData) {
      setFormatType(editData.type);
      if (editData.type === 'scale') {
        setScaleValue(Number(editData.value));
      } else if (editData.type === 'decimals') {
        setDecimalsValue(String(editData.value));
      } else if (editData.type === 'precision') {
        setPrecisionValue(String(editData.value));
      } else if (editData.type === 'format') {
        setFormatValue(String(editData.value));
      }
    } else if (show) {
      if (availableTypes.includes('scale')) {
        setFormatType('scale');
      } else if (availableTypes.includes('decimals')) {
        setFormatType('decimals');
      } else if (availableTypes.includes('precision')) {
        setFormatType('precision');
      } else if (availableTypes.includes('format')) {
        setFormatType('format');
      }
    }
  }, [show, editData, availableTypes]);

  const handleSubmit = () => {
    const buildData = (): { type: 'scale' | 'decimals' | 'precision' | 'format'; value: number | string } | null => {
      if (formatType === 'scale' && scaleValue > 0) {
        return { type: 'scale', value: scaleValue };
      } else if (formatType === 'decimals') {
        const value = parseAccuracyValue(decimalsValue);
        if (value !== null) return { type: 'decimals', value };
      } else if (formatType === 'precision') {
        const value = parseAccuracyValue(precisionValue);
        if (value !== null) return { type: 'precision', value };
      } else if (formatType === 'format' && formatValue) {
        return { type: 'format', value: formatValue };
      }
      return null;
    };

    const data = buildData();
    if (!data) return;

    if (isEditMode && onEdit && editData) {
      onEdit(data);
    } else {
      onAdd(data);
    }

    resetForm();
    onClose();
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) handleSubmit();
  };

  const resetForm = () => {
    setFormatType('decimals');
    setScaleValue(0);
    setDecimalsValue('0');
    setPrecisionValue('0');
    setFormatValue('');
  };

  const isValid =
    (formatType === 'scale' && scaleValue > 0) ||
    (formatType === 'decimals' && decimalsValue !== '' && isValidAccuracyValue(decimalsValue)) ||
    (formatType === 'precision' && precisionValue !== '' && isValidAccuracyValue(precisionValue)) ||
    (formatType === 'format' && formatValue);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <BSModal show={show} onHide={handleClose} contentClassName="taxonomy-modal">
      <BSModal.Body>
        <form onSubmit={handleFormSubmit} className="taxonomy-modal__content">
          <div className="taxonomy-modal__header">
            <Typography variant="body-8" fontWeight="semibold" className="taxonomy-modal__title">
              {isEditMode ? t('common:edit') : t('common:add')} {t('tag:property.label')}
            </Typography>
            <NewIconButton icon="close" variant="transparent" type="button" onClick={handleClose} className="modal-close" />
          </div>

          {!isEditMode && (
            <div className="form-group">
              <Typography variant="body-7" fontWeight="semibold" className="mb-2">
                {t('tag:type')}
              </Typography>
              <div className="radio-group">
                {availableTypes.includes('scale') && (
                  <label className="radio-label">
                    <input type="radio" value="scale" checked={formatType === 'scale'} onChange={(e) => setFormatType(e.target.value as 'scale')} />
                    <span>{t('tag:scale.title')}</span>
                  </label>
                )}
                {availableTypes.includes('decimals') && (
                  <label className="radio-label">
                    <input type="radio" value="decimals" checked={formatType === 'decimals'} onChange={(e) => setFormatType(e.target.value as 'decimals')} />
                    <span>{t('tag:accuracy.decimals')}</span>
                  </label>
                )}
                {availableTypes.includes('precision') && (
                  <label className="radio-label">
                    <input type="radio" value="precision" checked={formatType === 'precision'} onChange={(e) => setFormatType(e.target.value as 'precision')} />
                    <span>{t('tag:accuracy.title')}</span>
                  </label>
                )}
                {availableTypes.includes('format') && (
                  <label className="radio-label">
                    <input type="radio" value="format" checked={formatType === 'format'} onChange={(e) => setFormatType(e.target.value as 'format')} />
                    <span>{t('tag:format.title')}</span>
                  </label>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            {formatType === 'scale' ? (
              <input type="number" className="form-input" value={scaleValue || ''} onChange={(e) => setScaleValue(parseInt(e.target.value, 10) || 0)} min="1" />
            ) : formatType === 'decimals' ? (
              <>
                <input
                  type="text"
                  className="form-input"
                  value={decimalsValue}
                  onChange={(e) => setDecimalsValue(e.target.value)}
                  placeholder="-15 to 15 or INF"
                />
                {decimalsValue !== '' && !isValidAccuracyValue(decimalsValue) && (
                  <Typography variant="body-3" color="#d93025" className="mt-1">
                    {t('tag:accuracy.invalid')}
                  </Typography>
                )}
              </>
            ) : formatType === 'precision' ? (
              <>
                <input
                  type="text"
                  className="form-input"
                  value={precisionValue}
                  onChange={(e) => setPrecisionValue(e.target.value)}
                  placeholder="-15 to 15 or INF"
                />
                {precisionValue !== '' && !isValidAccuracyValue(precisionValue) && (
                  <Typography variant="body-3" color="#d93025" className="mt-1">
                    {t('tag:accuracy.invalid')}
                  </Typography>
                )}
              </>
            ) : (
              <NewDropdown
                options={FORMAT_OPTIONS}
                currentValue={formatValue || t('tag:format.choose')}
                onUpdate={(value: string) => setFormatValue(value)}
                variant="primary"
                fullwidth
              />
            )}
          </div>

          <div className="taxonomy-modal__footer">
            <button type="submit" className="btn btn-primary" disabled={!isValid}>
              {isEditMode ? t('common:save') : t('common:add')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              {t('common:cancel')}
            </button>
          </div>
        </form>
      </BSModal.Body>
    </BSModal>
  );
};
